'use client';

import { useState, FormEvent, useEffect, useCallback } from 'react';
import { useAuth } from '../../components/AuthProvider';
import Link from 'next/link';
import { useToast } from '../../components/Toast';
import { useRouter } from 'next/navigation';
import ConfirmationModal from '../../components/ConfirmationModal';
import { googleAuth } from '../../utils/googleAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Image from 'next/image';
import genplayLogo from '../../images/genplay_logo.png';
import {
  ACCOUNT_EXPIRED_CODE,
  ACCOUNT_EXPIRED_MESSAGE,
  AUTH_NOTICE_STORAGE_KEY
} from '../../lib/accountExpiry';

// Google API types
interface GoogleAPI {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      prompt: (callback?: (notification: GooglePromptNotification) => void) => void;
    };
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: GoogleTokenResponse) => void;
      }) => { requestAccessToken: () => void };
    };
  };
}

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleTokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
  error?: string;
}

interface GooglePromptNotification {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
}

const EMAIL_UNVERIFIED_CODE = 'EMAIL_UNVERIFIED';
const PENDING_APPROVAL_CODE = 'PENDING_APPROVAL';

interface EmailNotice {
  tone: 'success' | 'warning';
  title: string;
  text: string;
}

const PENDING_APPROVAL_NOTICE = (text?: string): EmailNotice => ({
  tone: 'warning',
  title: 'Account pending approval',
  text:
    text ||
    'Your email is confirmed, but your account is still pending approval. You will be able to log in once an administrator approves it.'
});

const VERIFIED_NOTICES: Record<string, EmailNotice> = {
  '1': {
    tone: 'success',
    title: 'Email confirmed',
    text: 'Thanks! Your account is now waiting for administrator approval. You will be able to log in once it is approved.'
  },
  invalid: {
    tone: 'warning',
    title: 'This link has expired or is invalid',
    text: 'Log in with your email and password and we will send you a new confirmation link.'
  }
};

export default function LoginPage() {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { login, completeLogin } = useAuth();
  const { showToast } = useToast();
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [googleUserInfo, setGoogleUserInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const router = useRouter();
  // Persistent "your account is disabled" notice — a toast would vanish before
  // the person understood why they cannot get in. Set by an expired login
  // attempt here, or handed over by AuthProvider after the session check signed
  // them out (docs/designs/account-expiry-dates.md).
  const [accountNotice, setAccountNotice] = useState<string | null>(null);
  // Signup email confirmation state: the ?verified= result of the link in the
  // email, or a login refused because the email is unconfirmed
  // (docs/designs/SIGNUP_EMAIL_VERIFICATION.md).
  const [emailNotice, setEmailNotice] = useState<EmailNotice | null>(null);

  useEffect(() => {
    const verified = new URLSearchParams(window.location.search).get('verified');
    if (verified) setEmailNotice(VERIFIED_NOTICES[verified] ?? VERIFIED_NOTICES.invalid);
  }, []);

  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(AUTH_NOTICE_STORAGE_KEY);
      if (notice) {
        setAccountNotice(notice);
        sessionStorage.removeItem(AUTH_NOTICE_STORAGE_KEY);
      }
    } catch {
      // sessionStorage unavailable (privacy mode) — nothing to show
    }
  }, []);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      showToast('Please enter both username and password', 'warning');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(username, password);

      if (!result.success) {
        if (result.code === ACCOUNT_EXPIRED_CODE) {
          setAccountNotice(result.error || ACCOUNT_EXPIRED_MESSAGE);
        } else if (result.code === EMAIL_UNVERIFIED_CODE) {
          setEmailNotice({
            tone: 'warning',
            title: 'Confirm your email',
            text: result.error || 'Please confirm your email address.'
          });
        } else if (result.code === PENDING_APPROVAL_CODE) {
          setEmailNotice(PENDING_APPROVAL_NOTICE(result.error));
        } else {
          showToast(result.error || 'Login failed', 'error');
        }
      }
      // If successful, the AuthProvider will handle navigation
    } catch (_error) {
      showToast('Login failed. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCallback = useCallback(
    async (response: GoogleCredentialResponse) => {
      try {
        const result = await fetch('/api/auth/google', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            credential: response.credential
          })
        });

        const data = await result.json();

        if (data.success) {
          showToast('Login successful!', 'success');
          // Use AuthProvider's completeLogin to handle authentication and redirect
          completeLogin(data.user);
        } else {
          setIsLoading(false);

          // Check for USER_NOT_FOUND error
          if (data.code === 'USER_NOT_FOUND' || data.error === 'User not registered') {
            // Decode the credential to extract Google user info for signup pre-fill
            try {
              let payload;
              if (response.credential.includes('.')) {
                payload = JSON.parse(atob(response.credential.split('.')[1]));
              } else {
                const decoded = atob(response.credential);
                const utf8Decoded = decodeURIComponent(
                  decoded
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
                );
                payload = JSON.parse(utf8Decoded);
              }
              const nameParts = payload.name ? payload.name.trim().split(' ') : ['', ''];
              setGoogleUserInfo({
                email: payload.email || '',
                firstName: nameParts[0] || '',
                lastName: nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
              });
            } catch {
              // If decoding fails, proceed without pre-fill
              setGoogleUserInfo(null);
            }
            setShowSignUpModal(true);
            return;
          }

          if (data.code === ACCOUNT_EXPIRED_CODE) {
            setAccountNotice(data.error || ACCOUNT_EXPIRED_MESSAGE);
            return;
          }

          showToast(data.error || 'Google login failed', 'error');
        }
      } catch (_error) {
        showToast('Google login failed', 'error');
        setIsLoading(false);
      }
    },
    [completeLogin, showToast]
  );

  // Initialize Google Sign-In
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      const google = (window as unknown as { google: GoogleAPI }).google;
      if (google && process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
        google.accounts.id.initialize({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
          auto_select: false,
          cancel_on_tap_outside: true
        });
      } else {
      }
    };

    // Load Google Identity Services script
    if (!(window as unknown as { google?: GoogleAPI }).google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeGoogleSignIn();
      };
      script.onerror = () => {
        showToast('Failed to load Google Sign-In', 'error');
      };
      document.head.appendChild(script);
    } else {
      initializeGoogleSignIn();
    }
  }, [handleGoogleCallback, showToast]);

  const handleGoogleLogin = () => {
    if (!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
      showToast('Google Client ID not configured', 'error');
      return;
    }

    const google = (window as unknown as { google: GoogleAPI }).google;
    if (google) {
      setIsLoading(true);
      try {
        // Use OAuth popup (full centered modal) instead of prompt (small notification)
        google.accounts.oauth2
          .initTokenClient({
            client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            callback: async (response: GoogleTokenResponse) => {
              if (response.access_token) {
                try {
                  // Store tokens for Google Drive access
                  googleAuth.storeTokens({
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    expires_in: response.expires_in || 3600,
                    token_type: 'Bearer',
                    scope: 'openid email profile'
                  });

                  // Pass access token directly — server will verify via Google userinfo API
                  handleGoogleCallback({ credential: response.access_token });
                } catch (_error) {
                  setIsLoading(false);
                  showToast('Failed to complete Google Sign-In', 'error');
                }
              } else {
                setIsLoading(false);
                showToast('Google Sign-In was cancelled', 'warning');
              }
            }
          })
          .requestAccessToken();
      } catch (_error) {
        setIsLoading(false);
        showToast('Failed to open Google Sign-In', 'error');
      }
    } else {
      showToast('Google Sign-In not loaded', 'error');
    }
  };

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg-primary)' }}
      >
        {/* Background Gaming Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-32 h-32 opacity-10">
            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg animate-pulse"></div>
          </div>
          <div className="absolute bottom-32 right-16 w-24 h-24 opacity-10">
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full animate-bounce"></div>
          </div>
          <div className="absolute top-1/2 right-1/4 w-16 h-16 opacity-10">
            <div className="w-full h-full bg-gradient-to-br from-green-500 to-emerald-500 transform rotate-45 animate-ping"></div>
          </div>
        </div>

        {/* Login Container */}
        <div className="relative z-10 w-full max-w-md mx-4">
          <div
            className="p-8 rounded-2xl glow"
            style={{
              background: 'var(--gradient-card)',
              border: '1px solid var(--border)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 flex items-center justify-center relative">
                  <Image src={genplayLogo} alt="GenPlay AI Logo" fill className="object-contain" />
                </div>
              </div>
              <h1
                className="text-3xl font-bold mb-2"
                style={{
                  color: 'var(--text-on-accent)',
                  background: 'var(--gradient-primary)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                GenPlay AI
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                Welcome to GenPlay AI
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                Build more interactive ads, faster
              </p>
            </div>

            {accountNotice && (
              <div
                role="alert"
                className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
              >
                <p className="font-semibold mb-1">Account disabled</p>
                <p>{accountNotice}</p>
              </div>
            )}

            {emailNotice && (
              <div
                role="status"
                className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
                  emailNotice.tone === 'success'
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                    : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
                }`}
              >
                <p className="font-semibold mb-1">{emailNotice.title}</p>
                <p>{emailNotice.text}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-6">
              {/* Username Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-lg"
                    style={{ color: 'var(--text-muted)' }}
                  />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full pl-12 pr-4 py-4 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200"
                  style={{
                    background: 'var(--bg-input)',
                    border: '2px solid var(--border)',
                    color: 'var(--text-primary)',
                    fontSize: '16px'
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = 'var(--primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(43, 183, 197, 0.1)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.boxShadow = 'none';
                  }}
                  disabled={isLoading}
                />
              </div>

              {/* Password Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon
                    icon={faLock}
                    className="text-lg"
                    style={{ color: 'var(--text-muted)' }}
                  />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-12 pr-12 py-4 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200"
                  style={{
                    background: 'var(--bg-input)',
                    border: '2px solid var(--border)',
                    color: 'var(--text-primary)',
                    fontSize: '16px'
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = 'var(--primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(43, 183, 197, 0.1)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.boxShadow = 'none';
                  }}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-lg"
                  style={{ color: 'var(--text-muted)' }}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm hover:underline transition-colors duration-200"
                  style={{ color: 'var(--primary)' }}
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--gradient-primary)',
                  color: 'var(--text-on-accent)'
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Logging in...
                  </span>
                ) : (
                  'Login'
                )}
              </button>

              {/* Divider */}
              <div className="relative flex items-center justify-center my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: 'var(--border)' }}></div>
                </div>
                <div
                  className="relative px-4"
                  style={{ background: 'var(--bg-card)', color: 'var(--text-muted)' }}
                >
                  OR
                </div>
              </div>

              {/* Google Login Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 hover:glow disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--bg-input)',
                  border: '2px solid var(--border)',
                  color: 'var(--text-primary)'
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Opening Google Sign-In...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg className="mr-3 w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </span>
                )}
              </button>

              {/* Domain Restriction Notice - REMOVED */}
              {/* <div className="text-center text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Only @unicostudio.co email addresses are allowed
              </div> */}
            </form>

            {/* Sign Up Link */}
            <div className="text-center mt-8">
              <span style={{ color: 'var(--text-secondary)' }}>
                Don&apos;t have an account yet?{' '}
              </span>
              <Link
                href="/signup"
                className={`font-semibold hover:underline transition-colors duration-200 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
                style={{ color: 'var(--primary)' }}
                aria-disabled={isLoading}
                onClick={e => {
                  if (isLoading) e.preventDefault();
                }}
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showSignUpModal}
        title="Account Not Found"
        message="It looks like you don't have an account yet. Please sign up first."
        confirmText="Go to Sign Up"
        cancelText="Close"
        type="info"
        onConfirm={() => {
          const params = new URLSearchParams();
          if (googleUserInfo?.email) params.set('email', googleUserInfo.email);
          if (googleUserInfo?.firstName) params.set('firstName', googleUserInfo.firstName);
          if (googleUserInfo?.lastName) params.set('lastName', googleUserInfo.lastName);
          const query = params.toString();
          router.push(`/signup${query ? `?${query}` : ''}`);
        }}
        onCancel={() => setShowSignUpModal(false)}
      />
    </>
  );
}
